import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
    import { Helmet } from "react-helmet";
import {
  FaBookOpen,
  FaCommentDots,
  FaHeadset,
  FaPhoneAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useTranslation } from "../../context/useTranslation";
import { useDarkMode } from "../../hooks/useDarkMode";

const Need = () => {
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [openSection, setOpenSection] = useState(null);

  const handleSubmit1 = (e) => {
    e.preventDefault();
    // console.log({
    //   rating,
    //   feedback,
    // });

    alert("Thanks for your feedback!");
    setRating(0);
    setFeedback("");
  };

  const options = [
    {
      type: "contact",
      icon: <FaHeadset className="text-3xl text-[#008300]" />,
      title: t("contact") || "Contact Support",
      desc:
        t("contactSupportDesc") ||
        "Reach out to our support team for technical help.",
    },
    {
      type: "guides",
      icon: <FaBookOpen className="text-3xl text-[#008300]" />,
      title: t("guides") || "Tutorials & Guides",
      desc:
        t("guidesDesc") || "Step-by-step tutorials to help you get started.",
    },
    {
      type: "feedback",
      icon: <FaCommentDots className="text-3xl text-[#008300]" />,
      title: t("feedback") || "Feedback",
      desc: t("feedbackDesc") || "Share your ideas and help us improve.",
    },
  ];

  const toggleSection = (type) => {
    if (type === "contact" || type === "feedback") {
      setOpenSection((prev) => (prev === type ? null : type));
    }
  };

  const handleChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you'd call an API; we'll simulate success
    // console.log("Submit contact", form);
    setSubmitted(true);
    setTimeout(() => setForm({ name: "", email: "", message: "" }), 400);
  };

  return (
    <>


<Helmet>

  {/* Title */}
  <title>Need Request | Seo Up Rank Dashboard</title>

  {/* Description */}
  <meta
    name="description"
    content="Submit your SEO needs or service requests through the Seo Up Rank dashboard to improve your website optimization and SEO performance."
  />

  {/* Private Page */}
  <meta name="robots" content="noindex, nofollow" />

  {/* Canonical */}
  <link rel="canonical" href="https://novarsis.ai/need" />

  {/* Open Graph */}
  <meta property="og:title" content="Seo Up Rank Need Request" />
  <meta
    property="og:description"
    content="Create and manage your SEO service requests from the Seo Up Rank dashboard."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://novarsis.ai/need" />
  <meta property="og:site_name" content="Seo Up Rank" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Seo Up Rank Need Request" />
  <meta
    name="twitter:description"
    content="Submit your SEO requirements using the Seo Up Rank dashboard."
  />

  {/* Schema */}
  <script type="application/ld+json">
    {`
    {
      "@context":"https://schema.org",
      "@type":"WebPage",
      "name":"Need Request - Seo Up Rank",
      "url":"https://novarsis.ai/need",
      "description":"User request page for submitting SEO needs in Seo Up Rank dashboard."
    }
    `}
  </script>

</Helmet>
  
    <div
      className={`min-h-[calc(100vh-120px)] px-4 sm:px-6 lg:px-8 py-8 sm:py-10 ${isDarkMode ? "bg-gray-950" : "bg-gray-50"}`}
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <h1
            className={`text-4xl sm:text-5xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {t("needHelp") || "Need Help?"}
          </h1>
          <p
            className={`mt-3 text-base sm:text-lg ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            {t("needHelpIntro") ||
              "Find answers, contact support, or share your feedback with us."}
          </p>
        </motion.div>

        <div className="space-y-6">
          {options.map((o, i) => {
            const isOpen = openSection === o.type;
            const isToggleable = o.type === "contact" || o.type === "feedback";

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.1 }}
                whileHover={{ y: -2 }}
                className={`rounded-xl p-6 sm:p-7 border-2 transition-all duration-300 ${isToggleable ? "cursor-pointer" : ""} ${isOpen ? (isDarkMode ? "bg-gray-800 border-[#008300] shadow-lg shadow-green-500/10" : "bg-white border-[#008300] shadow-lg shadow-green-500/20") : isDarkMode ? "bg-gray-800 border-gray-700 hover:border-gray-600" : "bg-white border-gray-200 hover:border-gray-300"}`}
                onClick={() => toggleSection(o.type)}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-green-900/40" : "bg-green-100"}`}
                  >
                    {o.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div
                        className={`text-lg sm:text-xl font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                      >
                        {o.title}
                      </div>
                      {isToggleable &&
                        (isOpen ? (
                          <FaChevronUp
                            className={`flex-shrink-0 ${isDarkMode ? "text-green-500" : "text-[#008300]"}`}
                          />
                        ) : (
                          <FaChevronDown
                            className={
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }
                          />
                        ))}
                    </div>
                    <div
                      className={`text-sm sm:text-base mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {o.desc}
                    </div>
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {o.type === "contact" && isOpen && (
                    <motion.div
                      key="contact"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                      className="mt-4 pt-4 overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h3
                        className={`text-2xl sm:text-3xl font-bold mb-6 text-center ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                      >
                        {t("contactSupport") || "Contact Support"}
                      </h3>

                      {submitted ? (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className={`p-4 rounded-lg text-center font-medium ${isDarkMode ? "bg-green-900/30 border border-green-500/30 text-green-300" : "bg-green-50 border border-green-200 text-green-800"}`}
                        >
                          ✓{" "}
                          {t("messageSent") ||
                            "Thanks — your message has been sent!"}
                        </motion.div>
                      ) : (
                        <form
                          onSubmit={handleSubmit}
                          className="space-y-5 w-full"
                        >
                          <div>
                            <label
                              className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {t("name") || "Name"}
                            </label>
                            <input
                              name="name"
                              value={form.name}
                              onChange={handleChange}
                              required
                              placeholder="Your full name"
                              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008300] focus:ring-offset-0 transition-colors ${isDarkMode ? "bg-gray-700 text-gray-200 border-gray-600 placeholder-gray-500" : "bg-white text-gray-800 border-gray-300 placeholder-gray-400"}`}
                            />
                          </div>

                          <div>
                            <label
                              className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {t("email") || "Email"}
                            </label>
                            <input
                              name="email"
                              value={form.email}
                              onChange={handleChange}
                              required
                              type="email"
                              placeholder="your.email@example.com"
                              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008300] focus:ring-offset-0 transition-colors ${isDarkMode ? "bg-gray-700 text-gray-200 border-gray-600 placeholder-gray-500" : "bg-white text-gray-800 border-gray-300 placeholder-gray-400"}`}
                            />
                          </div>

                          <div>
                            <label
                              className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {t("message") || "Message"}
                            </label>
                            <textarea
                              name="message"
                              value={form.message}
                              onChange={handleChange}
                              required
                              rows={5}
                              placeholder="Please describe your issue in detail..."
                              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008300] focus:ring-offset-0 transition-colors resize-none ${isDarkMode ? "bg-gray-700 text-gray-200 border-gray-600 placeholder-gray-500" : "bg-white text-gray-800 border-gray-300 placeholder-gray-400"}`}
                            />
                          </div>

                          <div className="flex items-center gap-3 pt-2">
                            <button
                              type="submit"
                              className="flex-1 bg-[#008300] text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:bg-[#006400] transition-all duration-200 hover:shadow-xl"
                            >
                              {t("submit") || "Send Message"}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setForm({ name: "", email: "", message: "" })
                              }
                              className={`px-5 py-3 rounded-lg font-medium transition-all duration-200 ${isDarkMode ? "text-gray-300 border-2 border-gray-600 hover:bg-gray-700" : "text-gray-700 border-2 border-gray-300 hover:bg-gray-100"}`}
                            >
                              {t("cancel") || "Clear"}
                            </button>
                          </div>
                        </form>
                      )}

                      <div
                        className={`mt-8 pt-6 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                      >
                        <h4
                          className={`text-lg font-semibold mb-6 text-center ${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
                        >
                          📞 Direct Contact
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <a
                            href="tel:+919111720303"
                            className={`flex items-center justify-center gap-3 p-4 rounded-lg transition-all duration-200 border-2 group ${isDarkMode ? "bg-gray-700/50 border-green-500/30 hover:bg-gray-700 hover:border-green-500" : "bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-400"}`}
                          >
                            <FaPhoneAlt className="text-[#008300] group-hover:scale-110 transition-transform text-lg" />
                            <span
                              className={`font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                            >
                              +91 9111720303
                            </span>
                          </a>
                          <a
                            href="tel:+916264059829"
                            className={`flex items-center justify-center gap-3 p-4 rounded-lg transition-all duration-200 border-2 group ${isDarkMode ? "bg-gray-700/50 border-green-500/30 hover:bg-gray-700 hover:border-green-500" : "bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-400"}`}
                          >
                            <FaPhoneAlt className="text-[#008300] group-hover:scale-110 transition-transform text-lg" />
                            <span
                              className={`font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                            >
                              +91 6264059829
                            </span>
                          </a>
                        </div>
                        <div
                          className={`mt-4 text-sm text-center ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                        >
                          {t("responseTime") ||
                            "⏱️ Typical response within 24 hours"}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {o.type === "feedback" && isOpen && (
                    <motion.div
                      key="feedback"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                      className="mt-4 pt-4 overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className={`max-w-2xl mx-auto rounded-xl p-6 sm:p-8 ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
                      >
                        <h2
                          className={`text-2xl sm:text-3xl font-bold mb-3 text-center ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                        >
                          📝 Share Your Feedback
                        </h2>
                        <p
                          className={`text-sm sm:text-base mb-6 text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Help us improve your experience by rating and sharing
                          your thoughts
                        </p>

                        <div className="flex gap-4 justify-center mb-8">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <motion.button
                              key={star}
                              type="button"
                              whileHover={{ scale: 1.2 }}
                              whileTap={{ scale: 0.9 }}
                              className={`text-5xl transition-all duration-200 filter ${(hover || rating) >= star ? "drop-shadow-lg" : ""}`}
                              onClick={() => setRating(star)}
                              onMouseEnter={() => setHover(star)}
                              onMouseLeave={() => setHover(0)}
                              aria-label={`Rate ${star} star`}
                            >
                              <span
                                className={`${(hover || rating) >= star ? "text-yellow-400" : isDarkMode ? "text-gray-600" : "text-gray-300"}`}
                              >
                                ★
                              </span>
                            </motion.button>
                          ))}
                        </div>

                        <textarea
                          className={`w-full border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008300] focus:ring-offset-0 p-4 text-sm sm:text-base font-normal ${isDarkMode ? "bg-gray-600 text-gray-200 border-gray-500 placeholder-gray-400" : "bg-white text-gray-800 border-gray-300 placeholder-gray-500"}`}
                          rows="6"
                          placeholder="Tell us what you think about our service..."
                          value={feedback}
                          onChange={(e) => setFeedback(e.target.value)}
                          required
                        />

                        <button
                          type="submit"
                          onClick={handleSubmit1}
                          className={`w-full mt-6 py-3 rounded-lg font-semibold transition-all duration-200 ${rating === 0 ? `${isDarkMode ? "bg-gray-500 text-gray-400 cursor-not-allowed" : "bg-gray-300 text-gray-500 cursor-not-allowed"}` : `bg-[#008300] text-white hover:bg-[#006400] shadow-lg hover:shadow-xl`}`}
                          disabled={rating === 0}
                        >
                          {rating === 0
                            ? "Select a rating to continue"
                            : "✓ Submit Feedback"}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>  </>
  );
};

export default Need;
